import React, { useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import {
  Security as SecurityIcon,
  Description as DocumentIcon,
  CheckCircle as ApproveIcon,
  AdminPanelSettings as AdminIcon,
  Check as CheckIcon,
  DirectionsCar as CarIcon,
  Announcement,
  HomeFilled,
  Logout,
  ContactPage,
} from "@mui/icons-material";

import { Layout, Menu, Avatar, Divider, Button, Tooltip } from "antd";

import {
  setPage,
  openMenuDrawer,
  closeMenuDrawer,
} from "../../state/slices/generalSlice";

import "../../styles/layout/navigationDrawer.scss";

const { Sider } = Layout;

const handleLogout = async () => {
  const token = localStorage.getItem("api_token");
  if (token) {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // Proceed with local logout even if server call fails
    }
  }
  localStorage.removeItem("api_token");
  window.location.href = "/";
};

const NavigationDrawer = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const hoverTimerRef = useRef(null);

  const { isDrawerMenuOpen } = useSelector((state) => state.generalSlice);
  const { currentPage } = useSelector((state) => state.generalSlice);
  const { account_types = [], details = {} } = useSelector(
    (state) => state.accountDetailSlice,
  );
  const isEmployeeInactive = details?.is_active === false;

  const token = localStorage.getItem("api_token");

  useEffect(() => {
    if (!token) {
      history.push("/login");
    }
  }, [token, history]);

  const mainMenuItems = useMemo(
    () => [
      { key: "dashboard", label: "Home", icon: <HomeFilled /> },
      {
        key: "faculty-profile",
        label: "Faculty Profile",
        icon: <ContactPage />,
      },
      { key: "doc-req", label: "Employee Submission", icon: <DocumentIcon /> },
      {
        key: "spectator",
        label: "Administrative Officer/Assistant",
        icon: <CheckIcon />,
      },
      { key: "approver", label: "Approve Documents", icon: <ApproveIcon /> },
      { key: "ebulletin", label: "eBulletin", icon: <Announcement /> },
      { key: "cybersecurity", label: "Cybersecurity", icon: <SecurityIcon /> },
      { key: "ouber-user-calendar", label: "OUber Services", icon: <CarIcon /> },
      // {
      //   key: "phishing-leaderboard",
      //   label: "Iskalerts",
      //   icon: <SecurityIcon />,
      // },
    ],
    [],
  );

  const adminMenuItems = useMemo(
    () => [
      {
        key: "positionsAdmin",
        label: "Employee Position Admin",
        icon: <AdminIcon />,
      },
      { key: "ebulAdmin", label: "eBulletin Admin", icon: <AdminIcon /> },
      { key: "superAdmin", label: "Super Admin", icon: <AdminIcon /> },
      // {
      //   key: "phishing-leaderboard",
      //   label: "Iskalerts",
      //   icon: <SecurityIcon />,
      // },
      {
        key: "ouber-admin",
        label: "OUber Admin",
        icon: <CarIcon />,
        children: [
          { key: "ouber-admin-calendar", label: "Calendar Overview" },
          { key: "ouber-admin-drivers", label: "Manage Drivers" },
          { key: "ouber-admin-vehicles", label: "Manage Vehicles" },
          { key: "ouber-admin-trip-requests", label: "Manage Trip Requests" },
        ]
      },
    ],
    [],
  );

  const roleMenuMap = useMemo(
    () => ({
      approver: ["approver"],
      employee: ["doc-req", "ouber-user-calendar"],
      faculty: ["faculty-profile"],
      ebulAdmin: ["ebulAdmin"],
      positionsAdmin: ["positionsAdmin"],
      superadmin: ["superAdmin", "ouber-admin"],
      spectator: ["spectator", "doc-req"],
      ouberAdmin: ["ouber-admin", "ouber-user-calendar"],
    }),
    [],
  );

  const alwaysVisibleKeys = useMemo(
    () => [
      "dashboard", 
      "ebulletin", 
      "cybersecurity", 
      "phishing-leaderboard", 
      "ouber-user-calendar",
    ],
    [],
  );


  const allowedKeys = useMemo(() => {
    const set = new Set(alwaysVisibleKeys);
    (account_types || []).forEach((role) => {
      (roleMenuMap[role] || []).forEach((k) => set.add(k));
    });
    return set;
  }, [account_types, roleMenuMap, alwaysVisibleKeys]);

  const filteredMain = useMemo(
    () => mainMenuItems.filter((item) => allowedKeys.has(item.key)),
    [mainMenuItems, allowedKeys],
  );

  const filteredAdmin = useMemo(
    () => adminMenuItems.filter((item) => allowedKeys.has(item.key)),
    [adminMenuItems, allowedKeys],
  );

  const handleMouseEnter = () => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      dispatch(openMenuDrawer());
    }, 100);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      dispatch(closeMenuDrawer());
    }, 100);
  };

  useEffect(() => {
    return () => clearTimeout(hoverTimerRef.current);
  }, []);

  const handleMenuClick = ({ key }) => {
    dispatch(setPage(key));
  };

  if (!token) return null;

  const displayName =
    details.last_name && details.first_name
      ? `${details.last_name}, ${details.first_name}`
      : details.email || "User";

  const collapsed = !isDrawerMenuOpen;

  return (
    <div className="pivot-nav-sider-wrapper" onMouseLeave={handleMouseLeave}>
      <Sider
        className="pivot-nav-sider"
        collapsed={collapsed}
        collapsedWidth={60}
        width={280}
        trigger={null}
        onMouseEnter={handleMouseEnter}
      >
        {/* UPOU logo */}
        <div className="pivot-nav-sider-toggle">
          <img
            src="/assets/upou-university-logo.png"
            alt="UPOU Logo"
            className="pivot-nav-sider-logo"
          />
          {!collapsed && (
            <img
              src="/assets/UPOU-text.png"
              alt="UPOU"
              className="pivot-nav-sider-logo-text"
            />
          )}
        </div>

        <Divider style={{ margin: "0 0 4px 0" }} />

        {/* Main menu */}
        {isEmployeeInactive ? (
          !collapsed && (
            <div className="inactive-drawer-message">
              Please contact HR if you want to access PIVOT.
            </div>
          )
        ) : (
          <Menu
            mode="inline"
            selectedKeys={[currentPage]}
            onClick={handleMenuClick}
            className="pivot-nav-sider-menu"
          >
            {filteredMain.map((item) => (
              <Menu.Item
                key={item.key}
                icon={
                  item.icon
                    ? React.cloneElement(item.icon, {
                        style: { fontSize: 20 },
                      })
                    : null
                }
              >
                {item.label}
              </Menu.Item>
            ))}
          </Menu>
        )}

        {/* Admin section */}
        {filteredAdmin.length > 0 && (
          <div className="pivot-nav-sider-admin">
            <Divider style={{ margin: "0 0 4px 0" }}>
              {!collapsed && (
                <span className="pivot-nav-sider-admin-label">Admin</span>
              )}
            </Divider>
            <Menu
              mode="inline"
              selectedKeys={[currentPage]}
              onClick={handleMenuClick}
              className="pivot-nav-sider-menu pivot-nav-sider-menu--admin"
            >
              {filteredAdmin.map((item) =>
                item.children ? (
                  <Menu.SubMenu
                    key={item.key}
                    icon={item.icon ? React.cloneElement(item.icon, { style: { fontSize: 20 } }) : null}
                    title={item.label}
                  >
                    {item.children.map((child) => (
                      <Menu.Item key={child.key}>{child.label}</Menu.Item>
                    ))}
                  </Menu.SubMenu>
                ) : (
                  <Menu.Item
                    key={item.key}
                    icon={
                      item.icon
                        ? React.cloneElement(item.icon, {
                            style: { fontSize: 20 },
                          })
                        : null
                    }
                  >
                    {item.label}
                  </Menu.Item>
                )
              )}
            </Menu>
          </div>
        )}

        {/* Profile + logout pinned to bottom */}
        <div className="pivot-nav-sider-footer">
          <Divider style={{ margin: "0 0 8px 0" }} />

          <div className="pivot-nav-sider-profile">
            <Avatar size={36}>
              {(details.first_name?.[0] || "U").toUpperCase()}
            </Avatar>
            {!collapsed && (
              <div className="pivot-nav-sider-profile-info">
                <div className="pivot-nav-sider-profile-name">
                  {displayName}
                </div>
                <div className="pivot-nav-sider-profile-email">
                  {details.email}
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: "8px 0 0" }}>
            {collapsed ? (
              <Tooltip title="Logout" placement="right">
                <Button
                  type="text"
                  className="pivot-nav-sider-logout-icon"
                  onClick={handleLogout}
                  icon={<Logout style={{ fontSize: 20 }} />}
                />
              </Tooltip>
            ) : (
              <Button
                block
                style={{ backgroundColor: "#862632", color: "white" }}
                onClick={handleLogout}
              >
                <Logout fontSize="12px" style={{ marginRight: 6 }} />
                Logout
              </Button>
            )}
          </div>
        </div>
      </Sider>
    </div>
  );
};

export default NavigationDrawer;
